import type { Theme } from '@mui/material/styles';
import { styled as muiStyled } from '@mui/material/styles';
import type { CreateMUIStyled } from '@mui/system';

/**
 * Creates a `shouldForwardProp` function that filters out:
 * - Any props that start with `$`
 *
 * If a user-defined `shouldForwardProp` function is provided,
 * it will be applied in addition to the default filtering.
 *
 * @param userFn - Optional user-defined function to further control prop forwarding
 * @returns A combined prop filtering function
 */
const defaultShouldForwardProp = (userFn?: (prop: string) => boolean) => {
  return (prop: string) => {
    if (prop.startsWith('$')) return false;
    return userFn ? userFn(prop) : true;
  };
};

/**
 * A wrapper around MUI's `styled` utility that automatically filters out:
 * - Props starting with `$`
 *
 * Optionally respects and combines user-defined `shouldForwardProp` logic.
 * This prevents unwanted props from being passed to the DOM and causing warnings.
 *
 * @template Component - The React component or intrinsic element to be styled
 * @template Options - The options object passed to MUI's `styled` utility
 *
 * @param component - The component or HTML tag to be styled
 * @param options - Optional configuration including custom `shouldForwardProp`
 * @returns A styled component with proper filtering and full typings
 */
const styledWithFilter = function styledWithFilter<
  Component extends Parameters<CreateMUIStyled<Theme>>[0],
  Options extends Parameters<CreateMUIStyled<Theme>>[1],
>(component: Component, options?: Options) {
  return muiStyled(component, {
    ...options,
    shouldForwardProp: defaultShouldForwardProp(options?.shouldForwardProp),
  });
} as CreateMUIStyled<Theme>;

/**
 * `styledCustom` is a custom wrapper around MUI's `styled` utility that:
 * - Automatically filters out props starting with `$`
 * - Fully supports the MUI `Theme` type
 *
 * @experimental  ⚠️ Experimental feature use with caution. ⚠️
 */
export const styledCustom = styledWithFilter;
